import classNames from "classnames";
import React from "react";
import intl from "react-intl-universal";


import {SelectOption} from "./Select";
import type {ContextMenuInterface} from "../../../elements/context-menu/ContextMenuInterface";


import {ContextMenuWithNavigation, ContextMenuWithNavigationItem} from "../../ContextMenuWithNavigation";
import {MoreVerticalIcon} from "../../icons/MoreVerticalIcon";


import {screenSizeProvider} from "../../../../stores/ScreenSizeProvider";


export interface SelectOptionsAdditionalBase {
    options: SelectOption[];
    maxHeight?: number;
    maxWidth?: number;
    allowFilter?: boolean;
    filterQuery?: string;
    headerNode?: React.ReactNode;
    withBorder?: boolean,
    shouldForcePlace?: boolean
}

export type SingleSelectOptionsAdditionalParams = SelectOptionsAdditionalBase & {
    allowMulti?: false;
    selectedValue: string | null;
    onChange: (selectedValue: string | null) => void;
}

export type MultiSelectOptionsAdditionalParams = SelectOptionsAdditionalBase & {
    allowMulti: true;
    selectedValues: string[] | null;
    onChange: (selectedValues: string[] | null) => void;
}

export type SelectOptionsAdditionalParams = SingleSelectOptionsAdditionalParams | MultiSelectOptionsAdditionalParams;

export type SelectOptionsProps = ContextMenuInterface & SelectOptionsAdditionalParams;


export const SelectOptions = (
    props: SelectOptionsProps
) => {
    const {
        handleClose,
        options,
        onChange,
        isDark,
        maxHeight,
        maxWidth,
        allowFilter = false,
        filterQuery,
        headerNode,
        withBorder,
    } = props;

    const isMobile = screenSizeProvider.view !== 'desktop';

    let selectedValues: string[] | null = null;
    const isMultiSelect = isMultiSelectParams(props)
    if (isMultiSelect) {
        selectedValues = props.selectedValues;
    } else {
        selectedValues = props.selectedValue === null ? null : [props.selectedValue];
    }

    const preparedOptions: ContextMenuWithNavigationItem[] = options
        .filter(option => !isMultiSelect || !selectedValues || !selectedValues?.includes(option.value))
        .map(option => ({
            id: option.value,
            getContent: (ctxMenuItem, meta, innerRef) => (
                <li
                    ref={innerRef}
                    key={option.value}
                    onClick={meta.onClick}
                    className={classNames({
                        'Select__option': true,
                        'Select__option--active': selectedValues?.includes(option.value),
                        'Select__option--loading': option.value === "loading",
                        'Select__option--isInNavigationFocus': meta.isInNavigationFocus && !isMobile,
                    })}
                >
                    <div className="Select_option_content ">
                        {option.Icon && (
                            <div className="Select_option_icon">
                                <option.Icon size={16}/>
                            </div>)
                        }
                        <div className="Select_option_header body-large">
                            <div className="Select_option__text">{option.label}</div>
                            {
                                option.activeIcon
                                    ? (
                                        <div className="Select_option_mark">
                                            <option.activeIcon size={16} additionalPadding={2}/>
                                        </div>
                                    )
                                    : null
                            }
                            {
                                option.moreAction
                                    ? (
                                        <div
                                            className="Select_option_moreAction"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                option.moreAction?.(e);
                                            }}
                                        >
                                            <MoreVerticalIcon size={20}/>
                                        </div>
                                    ) : null
                            }

                        </div>
                    </div>
                    {
                        option.description
                            ? (
                                <div className="Select_option_description body-small">
                                    {option.description}
                                </div>
                            )
                            : null
                    }
                </li>
            ),
            condition: option.condition,
            text: option.label,
            textForFilter: !option.textForFilter ? option.label as string : option.textForFilter,
            meta: option,
            preserveContextMenu: props.allowMulti,
        })
    )

    const handleSingleOptionChange = (itemOption: ContextMenuWithNavigationItem) => onChange(itemOption.meta.value)
    const handleMultiOptionChange = (itemOption: ContextMenuWithNavigationItem) => {
        if (selectedValues === null) {
            return (onChange as (selectedValues: string[] | null) => void)([itemOption.meta.value])
        }
        return (onChange as (selectedValues: string[] | null) => void)([...selectedValues, (itemOption.meta.value as string)])
    }

    return (
        <ContextMenuWithNavigation
            headerNode={headerNode}
            allowFilter={allowFilter}
            maxHeight={maxHeight ? `${maxHeight}px` : undefined}
            maxWidth={maxWidth ? `${maxWidth}px` : undefined}
            className="Select__options"
            handleClose={handleClose}
            withBorder={withBorder}
            itemGroups={[preparedOptions]}
            isDark={isDark}
            onItemClick={isMultiSelectParams(props) ? handleMultiOptionChange : handleSingleOptionChange}
            filterQuery={filterQuery}
            notFoundNode={(
                <div
                    key={'not_found'}
                    className='ContextMenuInEditor__not-found'
                >
                    {intl.get('trigger-actions.no_result_found').d('No results')}
                </div>
            )}
        />
    )
}

export function isMultiSelectParams(props: SelectOptionsAdditionalParams): props is MultiSelectOptionsAdditionalParams {
    return props.allowMulti === true;
}
